<template>
  <check-authorization :requiresAuthorizations="['listar roles']">
    <b-overlay :show="loading && !roles.length" rounded="sm">
      <b-row class="roles-screen">
        <!-- Lista de roles -->
        <b-col lg="4" xl="3" class="mb-4">
          <div class="card roles-list">
            <div class="card-header roles-list__header">
              <span>Roles</span>
              <b-button v-if="can('crear roles')" size="sm" variant="success" @click="openRoleModal(null)">
                <b-icon-plus /> Nuevo
              </b-button>
            </div>

            <div class="roles-list__items">
              <button
                v-for="role in roles"
                :key="role.id"
                type="button"
                class="roles-list__item"
                :class="{ 'roles-list__item--active': selected && selected.id === role.id }"
                @click="selectRole(role)"
              >
                <span class="roles-list__name">
                  {{ role.name }}
                  <b-badge v-if="role.name === 'admin'" variant="success" class="ml-1">todo</b-badge>
                </span>
                <span class="roles-list__meta">
                  {{ role.usuarios }} {{ role.usuarios === 1 ? 'usuario' : 'usuarios' }}
                  ·
                  <span :class="{ 'text-danger': !role.permisos.length }">
                    {{ role.permisos.length }} {{ role.permisos.length === 1 ? 'permiso' : 'permisos' }}
                  </span>
                </span>
              </button>
            </div>
          </div>
        </b-col>

        <!-- Permisos del rol seleccionado -->
        <b-col lg="8" xl="9" class="mb-4">
          <div v-if="selected" class="card">
            <div class="card-header roles-detail__header">
              <div class="roles-detail__title">
                <div class="roles-detail__name">{{ selected.name }}</div>
                <div class="roles-detail__description">
                  {{ selected.description || 'Sin descripción' }}
                </div>
              </div>

              <div class="roles-detail__actions">
                <b-button
                  v-if="can('editar roles') && !isAdmin"
                  size="sm"
                  variant="light"
                  @click="openRoleModal(selected)"
                >
                  <b-icon-pencil /> Renombrar
                </b-button>
                <b-button
                  v-if="can('eliminar roles') && !isAdmin"
                  size="sm"
                  variant="light"
                  class="text-danger"
                  @click="deleteRole"
                >
                  <b-icon-trash /> Eliminar
                </b-button>
                <b-button
                  v-if="editable"
                  size="sm"
                  variant="primary"
                  :disabled="!isDirty || saving"
                  @click="savePermisos"
                >
                  {{ saving ? 'Guardando…' : 'Guardar' }}
                </b-button>
              </div>
            </div>

            <div class="card-body">
              <div class="roles-detail__toolbar">
                <b-input-group size="sm" class="roles-detail__search">
                  <b-input-group-prepend is-text><b-icon-search /></b-input-group-prepend>
                  <b-form-input v-model="filter" type="search" placeholder="Buscar permiso" />
                </b-input-group>

                <b-form-checkbox v-model="onlyAssigned" switch size="sm">
                  Solo los que tiene
                </b-form-checkbox>

                <span class="roles-detail__count">
                  {{ checked.length }} de {{ totalPermisos }} permisos
                  <b-badge v-if="isDirty" variant="warning" class="ml-1">sin guardar</b-badge>
                </span>
              </div>

              <b-alert v-if="isAdmin" show variant="light" class="small">
                El rol <b>admin</b> siempre tiene todos los permisos; aquí solo se consultan.
              </b-alert>
              <b-alert v-else-if="!editable" show variant="light" class="small">
                Solo puedes consultar. Para cambiar permisos necesitas el permiso <b>editar roles</b>.
              </b-alert>

              <div class="roles-modules">
                <div v-for="modulo in visibleModulos" :key="modulo.nombre" class="roles-module">
                  <div class="roles-module__header">
                    <b-form-checkbox
                      :checked="moduleState(modulo) === 'all'"
                      :indeterminate="moduleState(modulo) === 'some'"
                      :disabled="!editable"
                      @change="toggleModule(modulo, $event)"
                    >
                      {{ modulo.nombre }}
                    </b-form-checkbox>
                    <span class="roles-module__count">
                      {{ countIn(modulo) }}/{{ modulo.permisos.length }}
                    </span>
                  </div>

                  <ul class="roles-module__list">
                    <li v-for="p in modulo.visibles" :key="p.id">
                      <b-form-checkbox
                        v-model="checked"
                        :value="p.id"
                        :disabled="!editable"
                        :title="p.name"
                      >
                        {{ p.description || p.name }}
                      </b-form-checkbox>
                    </li>
                  </ul>
                </div>
              </div>

              <p v-if="!visibleModulos.length" class="text-muted small text-center my-4">
                Ningún permiso coincide con la búsqueda.
              </p>
            </div>
          </div>
        </b-col>
      </b-row>
    </b-overlay>

    <!-- Crear / renombrar -->
    <b-modal
      v-model="roleModal.show"
      :title="roleModal.id ? 'Renombrar rol' : 'Nuevo rol'"
      size="md"
      centered
      no-close-on-backdrop
      @ok.prevent="saveRole"
    >
      <b-form @submit.prevent="saveRole">
        <b-form-group label="Nombre" :state="fieldState('name')" :invalid-feedback="fieldError('name')">
          <b-form-input v-model="roleModal.name" :state="fieldState('name')" autofocus maxlength="100" />
        </b-form-group>
        <b-form-group
          label="Descripción"
          description="Para qué sirve o a quién se le asigna."
          :state="fieldState('description')"
          :invalid-feedback="fieldError('description')"
        >
          <b-form-input v-model="roleModal.description" :state="fieldState('description')" maxlength="255" />
        </b-form-group>
        <p v-if="!roleModal.id" class="small text-muted mb-0">
          El rol se crea sin permisos; después los marcas y guardas.
        </p>
      </b-form>

      <template #modal-footer="{ ok, cancel }">
        <b-button size="sm" variant="light" @click="cancel">Cancelar</b-button>
        <b-button size="sm" variant="primary" :disabled="roleModal.saving || !roleModal.name" @click="ok">
          {{ roleModal.saving ? 'Guardando…' : 'Guardar' }}
        </b-button>
      </template>
    </b-modal>
  </check-authorization>
</template>

<script>
import { mapGetters } from 'vuex'

const normalize = text =>
  (text || '')
    .toString()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')

const sameIds = (a, b) => a.length === b.length && a.every(id => b.includes(id))

const roleModal = () => ({
  show: false,
  id: null,
  name: '',
  description: '',
  saving: false,
  errors: {}
})

export default {
  name: 'Roles',

  data () {
    return {
      loading: false,
      saving: false,
      roles: [],
      modulos: [],
      selected: null,
      checked: [],
      filter: '',
      onlyAssigned: false,
      roleModal: roleModal()
    }
  },

  computed: {
    ...mapGetters('userModule', ['getAuthPermissions']),

    isAdmin () {
      return Boolean(this.selected && this.selected.name === 'admin')
    },

    editable () {
      return this.can('editar roles') && !this.isAdmin
    },

    isDirty () {
      return Boolean(this.selected) && !sameIds(this.checked, this.selected.permisos)
    },

    totalPermisos () {
      return this.modulos.reduce((total, m) => total + m.permisos.length, 0)
    },

    visibleModulos () {
      const text = normalize(this.filter)

      return this.modulos
        .map(modulo => {
          const moduloCoincide = text && normalize(modulo.nombre).includes(text)
          const visibles = modulo.permisos.filter(p => {
            if (this.onlyAssigned && !this.checked.includes(p.id)) return false
            if (!text || moduloCoincide) return true
            return normalize(p.description).includes(text) || normalize(p.name).includes(text)
          })
          return { ...modulo, visibles }
        })
        .filter(m => m.visibles.length)
    }
  },

  created () {
    this.load()
  },

  methods: {
    can (permission) {
      return this.getAuthPermissions.includes(permission)
    },

    async load (selectId = null) {
      this.loading = true
      const { error, message, data } = await this.$store.dispatch('roleModule/getMatriz')
      this.loading = false

      if (error) return this.$notify({ error, message }, 'Roles')

      this.roles = data.data.roles
      this.modulos = data.data.modulos

      const id = selectId || (this.selected && this.selected.id)
      const role = this.roles.find(r => r.id === id) || this.roles.find(r => r.name !== 'admin') || this.roles[0]
      this.setSelected(role)
    },

    setSelected (role) {
      this.selected = role || null
      this.checked = role ? [...role.permisos] : []
    },

    async selectRole (role) {
      if (this.selected && role.id === this.selected.id) return
      if (this.isDirty && !(await this.confirm('Tienes cambios sin guardar en este rol. ¿Descartarlos?', 'Descartar'))) return

      this.setSelected(role)
    },

    countIn (modulo) {
      return modulo.permisos.filter(p => this.checked.includes(p.id)).length
    },

    moduleState (modulo) {
      const count = this.countIn(modulo)
      if (!count) return 'none'
      return count === modulo.permisos.length ? 'all' : 'some'
    },

    toggleModule (modulo, value) {
      // Con búsqueda activa, marca o desmarca solo los permisos visibles del módulo.
      const ids = (this.filter ? modulo.visibles : modulo.permisos).map(p => p.id)
      this.checked = value
        ? [...new Set([...this.checked, ...ids])]
        : this.checked.filter(id => !ids.includes(id))
    },

    async savePermisos () {
      this.saving = true
      const { error, message } = await this.$store.dispatch('roleModule/savePermisos', {
        id: this.selected.id,
        permisos: this.checked
      })
      this.saving = false

      this.$notify({ error, message }, 'Permisos del rol')
      if (error) return

      this.selected.permisos = [...this.checked]
    },

    openRoleModal (role) {
      this.roleModal = {
        ...roleModal(),
        show: true,
        id: role ? role.id : null,
        name: role ? role.name : '',
        description: role ? role.description || '' : ''
      }
    },

    fieldError (field) {
      const errors = this.roleModal.errors[field]
      return errors ? errors[0] : ''
    },

    fieldState (field) {
      return this.roleModal.errors[field] ? false : null
    },

    async saveRole () {
      if (!this.roleModal.name) return

      this.roleModal.saving = true
      const { error, message, data } = await this.$store.dispatch('roleModule/saveRole', {
        id: this.roleModal.id,
        name: this.roleModal.name.trim(),
        description: this.roleModal.description.trim()
      })
      this.roleModal.saving = false

      if (error) {
        this.roleModal.errors = (data && data.errors) || {}
        if (!data || !data.errors) this.$notify({ error, message }, 'Roles')
        return
      }

      this.$notify({ error, message: this.roleModal.id ? 'Rol renombrado' : 'Rol creado' }, 'Roles')

      const id = this.roleModal.id || (data && data.id)
      this.roleModal.show = false

      // Al renombrar se conservan las casillas que no se han guardado.
      const pendientes = this.isDirty ? [...this.checked] : null
      await this.load(id)
      if (pendientes && this.selected && this.selected.id === id) this.checked = pendientes
    },

    async deleteRole () {
      const role = this.selected
      const aviso = role.usuarios
        ? `${role.usuarios} ${role.usuarios === 1 ? 'usuario lo tiene y perderá' : 'usuarios lo tienen y perderán'} sus permisos. `
        : ''

      if (!(await this.confirm(`${aviso}¿Eliminar el rol "${role.name}"?`, 'Eliminar', 'danger'))) return

      const { error, message } = await this.$store.dispatch('roleModule/deleteRole', role.id)
      this.$notify({ error, message }, 'Roles')
      if (error) return

      this.selected = null
      this.load()
    },

    confirm (text, okTitle, okVariant = 'primary') {
      return this.$bvModal.msgBoxConfirm(text, {
        title: 'Confirmación',
        size: 'sm',
        buttonSize: 'sm',
        okVariant,
        okTitle,
        cancelTitle: 'Cancelar',
        footerClass: 'p-2',
        centered: true
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.roles-list {
  position: sticky;
  top: 5rem;
}

.roles-list__header,
.roles-detail__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.roles-list__items {
  max-height: calc(100vh - 12rem);
  overflow-y: auto;
  padding: 0.35rem;
}

.roles-list__item {
  display: block;
  width: 100%;
  text-align: left;
  background: none;
  border: 0;
  border-radius: 0.35rem;
  padding: 0.5rem 0.65rem;
  border-left: 3px solid transparent;

  &:hover {
    background: #f2f4f8;
  }

  &:focus {
    outline: none;
  }
}

.roles-list__item--active {
  background: #eef2ff;
  border-left-color: #0061f2;

  &:hover {
    background: #eef2ff;
  }
}

.roles-list__name {
  display: block;
  font-size: 0.85rem;
  font-weight: 500;
  color: #212832;
}

.roles-list__meta {
  display: block;
  font-size: 0.72rem;
  color: #69707a;
}

.roles-detail__header {
  flex-wrap: wrap;
}

.roles-detail__name {
  font-size: 1rem;
  font-weight: 600;
  color: #212832;
}

.roles-detail__description {
  font-size: 0.8rem;
  font-weight: 400;
  color: #69707a;
}

.roles-detail__actions {
  display: flex;
  gap: 0.4rem;
}

.roles-detail__toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem 1.25rem;
  margin-bottom: 1rem;
}

.roles-detail__search {
  max-width: 280px;
}

.roles-detail__count {
  margin-left: auto;
  font-size: 0.8rem;
  color: #69707a;
}

// Tarjetas en columnas tipo mosaico: cada módulo ocupa solo su alto.
.roles-modules {
  column-count: 1;
  column-gap: 1rem;

  @media (min-width: 768px) {
    column-count: 2;
  }

  @media (min-width: 1400px) {
    column-count: 3;
  }
}

.roles-module {
  break-inside: avoid;
  margin-bottom: 1rem;
  border: 1px solid #e3e6ec;
  border-radius: 0.4rem;
  overflow: hidden;
}

.roles-module__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 0.75rem;
  background: #f2f6fc;
  border-bottom: 1px solid #e3e6ec;
  font-size: 0.85rem;
  font-weight: 600;
  color: #212832;
}

.roles-module__count {
  font-size: 0.72rem;
  font-weight: 500;
  color: #69707a;
}

.roles-module__list {
  list-style: none;
  margin: 0;
  padding: 0.25rem 0.75rem;

  li {
    padding: 0.3rem 0;
    font-size: 0.8rem;
    border-bottom: 1px solid #f2f4f8;

    &:last-child {
      border-bottom: 0;
    }
  }
}
</style>
